/**
 * TreeNavigator Component
 *
 * Renders a collapsible folder tree for browsing email templates.
 * Uses the neigebaie TreeView component internally with per-item
 * context menus, drag-and-drop, and highlighted templates section.
 * Styled to match the OverlaySideNav component pattern.
 */

import { useState, useMemo, useCallback } from 'react'
import { motion } from 'motion/react'
import { Folder, FileText, Search, Star, X } from 'lucide-react'
import { Copy, FolderInput, Trash2, FilePlus, FolderPlus, PenLine } from 'lucide-react'
import { cn } from '@/lib/utils'
import { stripNodePrefix } from '@/lib/email-templates'
import TreeView from '@/components/tree-view'
import type { TreeViewItem, TreeViewMenuItem } from '@/components/tree-view'
import type { TemplateNode } from '@/lib/email-templates'

/** Template IDs to feature in the highlighted section */
const HIGHLIGHTED_TEMPLATE_IDS = ['launch-physician']

interface TreeNavigatorProps {
  tree: TemplateNode
  selectedId: string | null
  onSelect: (id: string) => void
  onDuplicateTemplate?: (templateKey: string) => void
  onMoveTemplate?: (templateKey: string, targetFolderId: number | null) => void
  onMoveFolder?: (folderId: number, targetParentId: number | null) => void
  onDeleteTemplate?: (templateKey: string) => void
  onDeleteFolder?: (folderId: number) => void
  onRequestMoveTo?: (nodeId: string) => void
  onCreateTemplateInFolder?: (folderId: number) => void
  onCreateFolderInFolder?: (folderId: number | null) => void
  onRenameTemplate?: (templateKey: string, currentName: string) => void
  onRenameFolder?: (folderId: number, currentName: string) => void
}

/**
 * Convert a TemplateNode tree to TreeViewItem[] (children of root).
 * Preserves the namespaced IDs (folder-X, template-Y) so that
 * stripNodePrefix can be used downstream.
 */
function mapNodeToTreeViewItem(node: TemplateNode): TreeViewItem {
  return {
    id: node.id,
    name: node.name,
    type: node.type === 'folder' ? 'folder' : 'file',
    children: node.children?.map(mapNodeToTreeViewItem),
  }
}

/**
 * Collect all folder IDs from a TemplateNode tree.
 */
function collectFolderIds(node: TemplateNode): string[] {
  const ids: string[] = []
  if (node.type === 'folder') {
    ids.push(node.id)
  }
  node.children?.forEach((child) => {
    ids.push(...collectFolderIds(child))
  })
  return ids
}

export function TreeNavigator({
  tree,
  selectedId,
  onSelect,
  onDuplicateTemplate,
  onMoveTemplate,
  onMoveFolder,
  onDeleteTemplate,
  onDeleteFolder,
  onRequestMoveTo,
  onCreateTemplateInFolder,
  onCreateFolderInFolder,
  onRenameTemplate,
  onRenameFolder,
}: TreeNavigatorProps): React.ReactNode {
  const [searchQuery, setSearchQuery] = useState('')

  // Collect all templates from tree for searching and highlighting
  const allTemplates = useMemo(() => {
    const templates: Array<{ node: TemplateNode; path: string[] }> = []
    function traverse(node: TemplateNode, path: string[]) {
      if (node.type === 'file' && node.template) {
        templates.push({ node, path })
      }
      node.children?.forEach((child) => traverse(child, [...path, node.name]))
    }
    tree.children?.forEach((child) => traverse(child, []))
    return templates
  }, [tree])

  // Highlighted templates
  const highlightedTemplates = useMemo(() => {
    return allTemplates.filter(
      ({ node }) => node.template && HIGHLIGHTED_TEMPLATE_IDS.includes(node.template.id)
    )
  }, [allTemplates])

  // Filter tree based on search query — mirrors original logic including template metadata search
  const filteredTree = useMemo(() => {
    if (!searchQuery.trim()) return tree

    const query = searchQuery.toLowerCase()

    function filterNode(node: TemplateNode): TemplateNode | null {
      if (node.type === 'file') {
        const matches =
          node.name.toLowerCase().includes(query) ||
          node.template?.subject?.toLowerCase().includes(query) ||
          node.template?.description?.toLowerCase().includes(query) ||
          node.template?.category?.toLowerCase().includes(query) ||
          node.template?.audience?.toLowerCase().includes(query)
        return matches ? node : null
      }

      const filteredChildren = node.children
        ?.map(filterNode)
        .filter((child): child is TemplateNode => child !== null)

      if (filteredChildren && filteredChildren.length > 0) {
        return { ...node, children: filteredChildren }
      }
      return null
    }

    const filteredChildren = tree.children
      ?.map(filterNode)
      .filter((child): child is TemplateNode => child !== null)

    return { ...tree, children: filteredChildren }
  }, [tree, searchQuery])

  const hasResults = (filteredTree.children?.length ?? 0) > 0

  // Convert filtered tree to TreeViewItem[] (root children only — TreeView gets an array)
  const treeViewData = useMemo<TreeViewItem[]>(() => {
    if (!filteredTree.children) return []
    return filteredTree.children.map(mapNodeToTreeViewItem)
  }, [filteredTree])

  // When searching, force-expand all folders so results are visible
  const forceExpandedIds = useMemo(() => {
    if (!searchQuery.trim()) return undefined
    return new Set(collectFolderIds(filteredTree))
  }, [searchQuery, filteredTree])

  // Map the selected template ID back to the namespaced tree node ID
  // The parent passes selectedId as a raw template key (e.g., "launch-physician")
  // but tree nodes use "template-launch-physician"
  const controlledSelectedId = useMemo(() => {
    if (!selectedId) return null
    // Check if a template matches
    const templateNodeId = `template-${selectedId}`
    return templateNodeId
  }, [selectedId])

  // Icon map for the TreeView
  const iconMap = useMemo(() => ({
    folder: <Folder className="w-4 h-4 text-gray-400" />,
    file: <FileText className="w-4 h-4 text-gray-400" />,
  }), [])

  // Per-item context menu builder
  const getMenuItems = useCallback((item: TreeViewItem): TreeViewMenuItem[] => {
    const { type: nodeType, id: rawId } = stripNodePrefix(item.id)

    // Root — only create actions
    if (nodeType === 'root') {
      const items: TreeViewMenuItem[] = []
      if (onCreateTemplateInFolder) {
        items.push({
          id: 'new-template',
          label: 'New Template',
          icon: <FilePlus className="w-4 h-4" />,
          action: () => onCreateTemplateInFolder(0), // 0 signals root
        })
      }
      if (onCreateFolderInFolder) {
        items.push({
          id: 'new-folder',
          label: 'New Folder',
          icon: <FolderPlus className="w-4 h-4" />,
          action: () => onCreateFolderInFolder(null),
        })
      }
      return items
    }

    const items: TreeViewMenuItem[] = []

    // Create actions — folders only
    if (nodeType === 'folder') {
      if (onCreateTemplateInFolder) {
        items.push({
          id: 'new-template',
          label: 'New Template Here',
          icon: <FilePlus className="w-4 h-4" />,
          action: () => onCreateTemplateInFolder(parseInt(rawId, 10)),
        })
      }
      if (onCreateFolderInFolder) {
        items.push({
          id: 'new-folder',
          label: 'New Folder Here',
          icon: <FolderPlus className="w-4 h-4" />,
          action: () => onCreateFolderInFolder(parseInt(rawId, 10)),
        })
      }
    }

    // Rename — both templates and folders
    if (nodeType === 'template' && onRenameTemplate) {
      items.push({
        id: 'rename',
        label: 'Rename',
        icon: <PenLine className="w-4 h-4" />,
        action: () => onRenameTemplate(rawId, item.name),
      })
    } else if (nodeType === 'folder' && onRenameFolder) {
      items.push({
        id: 'rename',
        label: 'Rename',
        icon: <PenLine className="w-4 h-4" />,
        action: () => onRenameFolder(parseInt(rawId, 10), item.name),
      })
    }

    // Duplicate — templates only
    if (nodeType === 'template' && onDuplicateTemplate) {
      items.push({
        id: 'duplicate',
        label: 'Duplicate',
        icon: <Copy className="w-4 h-4" />,
        action: () => onDuplicateTemplate(rawId),
      })
    }

    // Move to — both templates and folders
    if ((nodeType === 'template' || nodeType === 'folder') && onRequestMoveTo) {
      items.push({
        id: 'move-to',
        label: 'Move to...',
        icon: <FolderInput className="w-4 h-4" />,
        action: () => onRequestMoveTo(item.id),
      })
    }

    // Delete — both templates and folders
    if (nodeType === 'template' && onDeleteTemplate) {
      items.push({
        id: 'delete',
        label: 'Delete',
        icon: <Trash2 className="w-4 h-4" />,
        action: () => onDeleteTemplate(rawId),
      })
    } else if (nodeType === 'folder' && onDeleteFolder) {
      items.push({
        id: 'delete',
        label: 'Delete',
        icon: <Trash2 className="w-4 h-4" />,
        action: () => onDeleteFolder(parseInt(rawId, 10)),
      })
    }

    return items
  }, [onDuplicateTemplate, onRequestMoveTo, onDeleteTemplate, onDeleteFolder, onCreateTemplateInFolder, onCreateFolderInFolder, onRenameTemplate, onRenameFolder])

  // Handle selection from TreeView
  const handleSelectionChange = useCallback((selectedItems: TreeViewItem[]) => {
    if (selectedItems.length === 0) return
    const item = selectedItems[0]
    onSelect(item.id) // pass namespaced ID — parent's handleSelectTemplate uses stripNodePrefix
  }, [onSelect])

  // Handle drag-and-drop
  const handleDrop = useCallback((draggedItem: TreeViewItem, targetItem: TreeViewItem) => {
    const { type: dragType, id: dragId } = stripNodePrefix(draggedItem.id)
    const { type: targetType, id: targetId } = stripNodePrefix(targetItem.id)

    // Target must be a folder (or root)
    if (targetType !== 'folder' && targetType !== 'root') return

    // Determine target folder ID (null for root)
    const targetFolderId = targetType === 'root'
      ? null
      : parseInt(targetId, 10)

    // Don't drop on self
    if (draggedItem.id === targetItem.id) return

    // Don't drop a folder into itself
    if (dragType === 'folder' && targetFolderId !== null) {
      const dragFolderId = parseInt(dragId, 10)
      if (dragFolderId === targetFolderId) return
    }

    if (dragType === 'template' && onMoveTemplate) {
      onMoveTemplate(dragId, targetFolderId)
    } else if (dragType === 'folder' && onMoveFolder) {
      onMoveFolder(parseInt(dragId, 10), targetFolderId)
    }
  }, [onMoveTemplate, onMoveFolder])

  return (
    <div className="h-full flex flex-col">
      {/* Search Field */}
      <div className="p-4 pb-3 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className={cn(
              'w-full pl-9 pr-9 py-2.5 text-sm rounded-xl',
              'bg-gray-50 border border-gray-200',
              'placeholder:text-gray-400 text-eventus-navy',
              'focus:outline-none focus:ring-2 focus:ring-eventus-purple/30 focus:border-eventus-purple/50',
              'transition-all duration-200'
            )}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-gray-200 transition-colors"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Highlighted Items Section */}
        {!searchQuery && highlightedTemplates.length > 0 && (
          <div className="p-4 pb-2">
            <h2 className="text-[10px] font-bold text-eventus-purple uppercase tracking-widest mb-3 flex items-center gap-1.5">
              <Star className="w-3 h-3 fill-eventus-purple/30" />
              Highlighted
            </h2>
            <div className="space-y-1">
              {highlightedTemplates.map(({ node }) => (
                <motion.button
                  key={node.id}
                  onClick={() => node.template && onSelect(`template-${node.template.id}`)}
                  whileHover={{ x: 2 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    'w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all duration-200',
                    'flex items-center gap-2.5',
                    'bg-gradient-to-r from-eventus-purple/5 to-eventus-teal/5',
                    'border border-eventus-purple/10 hover:border-eventus-purple/30',
                    'hover:shadow-sm',
                    selectedId === node.template?.id
                      ? 'bg-eventus-purple/10 border-eventus-purple/40 shadow-sm'
                      : ''
                  )}
                >
                  <span
                    className={cn(
                      'flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center',
                      'bg-eventus-purple/10',
                      selectedId === node.template?.id ? 'bg-eventus-purple/20' : ''
                    )}
                  >
                    <FileText
                      className={cn(
                        'w-3.5 h-3.5',
                        selectedId === node.template?.id
                          ? 'text-eventus-purple'
                          : 'text-eventus-purple/60'
                      )}
                    />
                  </span>
                  <div className="flex-1 min-w-0">
                    <span
                      className={cn(
                        'block truncate font-medium',
                        selectedId === node.template?.id
                          ? 'text-eventus-purple'
                          : 'text-eventus-navy'
                      )}
                    >
                      {node.name}
                    </span>
                    <span className="block text-[10px] text-gray-400 truncate capitalize">
                      {node.template?.audience} &bull; {node.template?.category}
                    </span>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* Folder Tree via neigebaie TreeView */}
        <div className="p-4 pt-2">
          <h2 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
            {searchQuery ? 'Results' : 'All Templates'}
          </h2>

          {hasResults ? (
            <TreeView
              className="border-none shadow-none p-0 bg-transparent"
              data={treeViewData}
              iconMap={iconMap}
              showExpandAll={false}
              showSearch={false}
              getMenuItems={getMenuItems}
              onSelectionChange={handleSelectionChange}
              isDraggable
              onDrop={handleDrop}
              forceExpandedIds={forceExpandedIds}
              controlledSelectedId={controlledSelectedId}
            />
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gray-100 flex items-center justify-center">
                <Search className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500">No templates found</p>
              <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
